// Typed links into the public product documentation (`product-docs/`).
//
// In-app help NEVER hand-pastes a docs URL. Every call site names a page from
// `DOC_PAGES`, so a renamed or deleted page is a TypeScript error at build
// time instead of a 404 a customer discovers. `web/tests/lib/docLinks.test.ts`
// pins the union to the actual `.mdx` files under
// `product-docs/content/docs/`, so the map cannot rot in either direction.
//
// Page ids are the site's URL slugs, which Fumadocs derives from the file path
// under `content/docs` (`product-docs/source.config.ts` +
// `app/docs/[[...slug]]/page.tsx`): drop `.mdx`, and an `index.mdx` collapses
// to its directory ("" for the docs root).
//
// The origin comes from runtime config (`helpLinks.documentationUrl`) so a
// self-managed install can point in-app help at its own mirror.
import { getRuntimeConfig } from "../config";

// ASSUMED(the variable feature uses one concept page, one task guide, and one
// lookup page.)
export const DOC_PAGES = [
  "",
  "account/api-keys",
  "account/billing",
  "account/members-and-roles",
  "account/organizations-and-teams",
  "account/phone-numbers",
  "account/retention-and-audit",
  "api",
  "api/authentication",
  "api/errors",
  "api/idempotency",
  "api/pagination",
  "backup-restore-dr",
  "concepts",
  "concepts/alert-rules",
  "concepts/agents-and-versions",
  "concepts/alignment",
  "concepts/assertions",
  "concepts/markers",
  "concepts/marks-labels-answers",
  "concepts/review",
  "concepts/simulated-testing",
  "concepts/traces-and-notes",
  "concepts/transcripts",
  "concepts/variables",
  "deploy",
  "deploy/air-gapped",
  "deploy/bring-your-own-models",
  "deploy/configuration",
  "deploy/identity-sso",
  "deploy/metrics-reference",
  "deploy/monitoring",
  "deploy/operations",
  "deploy/prerequisites",
  "deploy/registry-mirroring",
  "deploy/secrets",
  "deploy/self-managed",
  "deploy/support",
  "deploy/uninstall",
  "deploy/upgrade-rollback",
  "guides/alert-email",
  "guides/alert-incident-io",
  "guides/alert-pagerduty",
  "guides/alert-slack",
  "guides/alert-webhook",
  "guides/author-a-persona",
  "guides/author-a-scenario",
  "guides/built-in-markers",
  "guides/choosing-a-judge-model",
  "guides/configure-agent-markers",
  "guides/connect-livekit",
  "guides/connect-pipecat-cloud",
  "guides/create-an-agent",
  "guides/define-a-marker",
  // OPEN(metrics-metric-tokens-786): Marker-specific gate examples await
  // docs/open-questions.md#metrics-metric-tokens-786.
  // API CI is supported; #2479 blocks bootstrap; #1835 stays deferred.
  "guides/gate-releases",
  "guides/llm-judge-markers",
  "guides/rerun-markers",
  "guides/review-and-label",
  "guides/run-a-simulated-test",
  "guides/submit-a-transcript",
  "guides/submit-a-voice-call",
  "guides/track-results",
  "guides/use-variables-in-simulations",
  "helm-production-install",
  "how-marker-works",
  "quickstart",
  "reference/alert-message-tokens",
  "reference/built-in-marker-catalog",
  "reference/error-codes",
  "reference/glossary",
  "reference/roadmap",
  "reference/variable-resolution",
  "security-architecture",
  "security-posture",
  "sizing-support",
] as const;

/** A page that exists under `product-docs/content/docs/`. */
export type DocPage = (typeof DOC_PAGES)[number];

// Mirrors the `helpLinks.documentationUrl` default in `web/src/config.ts`;
// used only when an install injects an empty override.
const DOCS_FALLBACK_ORIGIN = "https://docs.usemarker.ai";

// Fumadocs serves the Documentation tab under `/docs`.
const DOCS_PATH_PREFIX = "/docs";

function docsBaseUrl(): string {
  const configured = getRuntimeConfig().helpLinks.documentationUrl?.trim();
  const base =
    configured !== undefined && configured.length > 0
      ? configured
      : DOCS_FALLBACK_ORIGIN;
  return base.replace(/\/+$/, "");
}

/** Absolute origin of the configured product-docs site. */
export function docsOrigin(): string {
  return docsBaseUrl();
}

/** Absolute URL of a product-docs page. */
export function docUrl(page: DocPage): string {
  const base = `${docsOrigin()}${DOCS_PATH_PREFIX}`;
  return page.length === 0 ? base : `${base}/${page}`;
}
